import type {
  AlertItem,
  ChartVisualisation,
  EngagementGroup,
  EngagementGroupRow,
  EngagementPoint,
  FeedEntry,
  KpiTile,
  MarketingData,
  ProfitabilityBucket,
  RowsPayload,
  SeriesGranularity,
  SnapshotPayload,
  TrendPoint,
  WidgetConfig,
  WidgetInstance,
  WidgetPayload,
} from "../../../../components/widgets/types"
import type {
  CatalogWidgetSpec,
  ResolvedWidget,
  WidgetCatalog,
  WidgetInstanceV4,
} from "../../redux/widgets.types"

export const CASH_FLOW_SLUG = "financial.cash_flow_series.chart"
export const PROJECT_STATUS_SLUG = "projects.project_status_breakdown.chart"
export const FIXED_WIDGET_SLUGS: readonly string[] = [
  CASH_FLOW_SLUG,
  PROJECT_STATUS_SLUG,
]

export const cashFlowInstance = (): WidgetInstanceV4 => ({
  id: CASH_FLOW_SLUG,
  slug: CASH_FLOW_SLUG,
  type: "chart",
  dataSource: "financial",
  config: { metric: "cash_flow_series", visualisation: "area" },
})

export const projectStatusInstance = (): WidgetInstanceV4 => ({
  id: PROJECT_STATUS_SLUG,
  slug: PROJECT_STATUS_SLUG,
  type: "chart",
  dataSource: "projects",
  config: { metric: "project_status_breakdown", visualisation: "donut" },
})

export const fixedWidgetInstances = (): WidgetInstanceV4[] => [
  cashFlowInstance(),
  projectStatusInstance(),
]

// The backend drops an always-on slot whose domain the caller cannot read, so
// the client-side injection has to make the same call or it puts the empty
// card straight back.
export const fixedWidgetInstancesFor = (readsFinance: boolean): WidgetInstanceV4[] =>
  fixedWidgetInstances().filter(
    (instance) => instance.dataSource !== "financial" || readsFinance
  )

export const slugFor = (
  dataSource: string,
  metric: string | null | undefined,
  type: string
): string => `${dataSource}.${metric ?? ""}.${type}`

// Cross-view value identity: the same (type, metric) resolves to the same value
// under a given window+filters context, regardless of which view hosts it.
export const valueKey = (type: string, metric: string | null | undefined): string =>
  `${type}::${metric ?? ""}`

const metricOf = (resolved: ResolvedWidget): string | undefined => {
  const fromConfig = resolved.config?.metric
  if (typeof fromConfig === "string") return fromConfig
  const fromPayload = (resolved.payload as { metric?: unknown })?.metric
  return typeof fromPayload === "string" ? fromPayload : undefined
}

export const resolvedValueKey = (resolved: ResolvedWidget): string =>
  valueKey(resolved.type, metricOf(resolved))

export const instanceValueKey = (instance: WidgetInstanceV4): string =>
  valueKey(instance.type, (instance.config?.metric as string | undefined) ?? null)

export interface SpecIndex {
  byMetric: Map<string, CatalogWidgetSpec>
  bySlug: Map<string, CatalogWidgetSpec>
}

// A resolved id is "<source>.<metric>.<type>", but the backend renders a null
// metric as the literal "None", so a metric-less id never equals its slug.
const NULL_METRIC_SEGMENTS = new Set(["", "None", "null", "undefined"])

const idParts = (id: string | undefined): { source: string; metric: string } | null => {
  const parts = (id ?? "").split(".")
  if (parts.length < 3) return null
  return { source: parts[0], metric: parts.slice(1, -1).join(".") }
}

const specFor = (
  resolved: ResolvedWidget,
  specIndex: SpecIndex
): CatalogWidgetSpec | undefined => {
  const parts = idParts(resolved.id)
  const metric =
    metricOf(resolved) ??
    (parts && !NULL_METRIC_SEGMENTS.has(parts.metric) ? parts.metric : undefined)
  if (metric) return specIndex.byMetric.get(metric)
  if (!parts) return undefined
  return specIndex.bySlug.get(slugFor(parts.source, null, resolved.type))
}

const fallbackTitle = (resolved: ResolvedWidget): string =>
  humanizeMetric(metricOf(resolved) ?? idParts(resolved.id)?.source ?? resolved.type)

// Identity only — the ADD surfaces read `catalog.widgets` directly, so indexing
// the restricted specs here never offers them. Leaving them out cost a resolved
// widget its data source: `resolvedSlug` fell back to an empty one, producing a
// slug that matches nothing in the layout, and `responseToInstances` dropped the
// widget outright, so a saved layout lost it on the next save instead of keeping
// the slot and rendering the "no access" state the backend sends.
export const indexCatalog = (catalog: WidgetCatalog | null): SpecIndex => {
  const byMetric = new Map<string, CatalogWidgetSpec>()
  const bySlug = new Map<string, CatalogWidgetSpec>()
  const specs = [...(catalog?.widgets ?? []), ...(catalog?.restricted_widgets ?? [])]
  for (const spec of specs) {
    if (spec.metric) byMetric.set(spec.metric, spec)
    bySlug.set(slugFor(spec.data_source, spec.metric, spec.type), spec)
  }
  return { byMetric, bySlug }
}

export const humanizeMetric = (metric: string): string =>
  metric
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const toKpiPayload = (
  resolved: ResolvedWidget,
  spec: CatalogWidgetSpec | undefined,
  currency: string | undefined
): WidgetPayload => {
  const p = resolved.payload as {
    value?: number | string
    unit?: string | null
    has_data?: boolean
    trend?: KpiTile["trend"]
    compare?: KpiTile["compare"]
    filters_ignored?: KpiTile["filters_ignored"]
    empty_reason?: KpiTile["empty_reason"]
  }
  const metric = metricOf(resolved) ?? resolved.id
  const tile: KpiTile = {
    id: metric,
    // The catalog can lag or fail on a cold landing; never surface the raw key.
    title: spec?.title ?? humanizeMetric(metric),
    value: p.value ?? "—",
    unit: p.unit ?? spec?.default_config?.unit as string | undefined,
    drill_to: resolved.drill_to || spec?.drill_to || "",
    icon: spec?.icon ?? undefined,
    trend: p.trend ?? null,
    has_data: p.has_data,
    // The explanation is static and rides the catalog spec; the skipped-filter
    // list is per-resolve and rides the payload.
    description: spec?.description,
    formula: spec?.formula,
    basis: spec?.basis,
    ...(p.filters_ignored?.length ? { filters_ignored: p.filters_ignored } : {}),
    ...(p.empty_reason ? { empty_reason: p.empty_reason } : {}),
    ...(p.compare ? { compare: p.compare } : {}),
  }
  return { tile, currency }
}

const isCashFlowSeries = (
  series: Record<string, unknown>[]
): series is { date: string; in: number; out: number; net: number }[] => {
  const first = series[0]
  return first != null && ("in" in first || "out" in first || "net" in first)
}

const toChartPayload = (resolved: ResolvedWidget): WidgetPayload => {
  const p = resolved.payload as {
    series?: Record<string, unknown>[]
    buckets?: ProfitabilityBucket[]
    currency?: string
    granularity?: SeriesGranularity
    as_of?: string
    on_track?: number
    at_risk?: number
    delayed?: number
    total?: number
    groups?: Partial<Record<EngagementGroup, EngagementGroupRow[]>>
    default_group?: EngagementGroup
    marketing?: MarketingData | null
    totals?: {
      new?: number
      engaged?: number
      rate?: number | string
      cohort_engaged?: number
    }
  }
  const visualisation = (resolved.config?.visualisation as ChartVisualisation) ?? undefined
  if ("marketing" in p) {
    return { chart: { visualisation: visualisation ?? "bar", marketing: p.marketing ?? null } }
  }
  if (p.totals && Array.isArray(p.series)) {
    return {
      chart: {
        visualisation: visualisation ?? "bar",
        engagement: {
          series: p.series as unknown as EngagementPoint[],
          granularity: p.granularity,
          groups: p.groups,
          defaultGroup: p.default_group,
          totals: {
            new: Number(p.totals.new ?? 0),
            engaged: Number(p.totals.engaged ?? 0),
            rate: Number(p.totals.rate ?? 0),
            cohort_engaged: Number(p.totals.cohort_engaged ?? 0),
          },
        },
      },
    }
  }
  if (Array.isArray(p.buckets)) {
    return {
      chart: {
        visualisation: visualisation ?? "bar",
        breakdown: { buckets: p.buckets, total: p.total, currency: p.currency },
      },
    }
  }
  if (Array.isArray(p.series)) {
    if (p.series.length > 0 && !isCashFlowSeries(p.series)) {
      return {
        chart: {
          visualisation: visualisation ?? "line",
          trend: { series: p.series as unknown as TrendPoint[], currency: p.currency },
        },
      }
    }
    return {
      chart: {
        visualisation: visualisation ?? "composed",
        cashFlow: {
          series: p.series as unknown as {
            date: string
            in: number
            out: number
            net: number
          }[],
          currency: p.currency,
          granularity: p.granularity,
          asOf: p.as_of,
        },
      },
    }
  }
  return {
    chart: {
      visualisation: visualisation ?? "donut",
      status: {
        on_track: p.on_track ?? 0,
        at_risk: p.at_risk ?? 0,
        delayed: p.delayed ?? 0,
        total: p.total ?? 0,
      },
    },
  }
}

const totalOf = (payload: Record<string, unknown>): number | undefined => {
  const total = (payload as { total?: unknown }).total
  if (typeof total === "number") return total
  const unfiltered = (payload as { total_unfiltered?: unknown }).total_unfiltered
  return typeof unfiltered === "number" ? unfiltered : undefined
}

const toFeedPayload = (resolved: ResolvedWidget): WidgetPayload => {
  const items = (resolved.payload as { items?: FeedEntry[] })?.items ?? []
  return { feed: { items, total: totalOf(resolved.payload) } }
}

const toAlertPayload = (resolved: ResolvedWidget): WidgetPayload => {
  const items = (resolved.payload as { items?: AlertItem[] })?.items ?? []
  return { alert: { items, total: totalOf(resolved.payload) } }
}

const toRowsPayload = (
  resolved: ResolvedWidget,
  currency: string | undefined
): WidgetPayload => {
  const p = resolved.payload as Partial<RowsPayload> & { currency?: string }
  const rows: RowsPayload = {
    columns: Array.isArray(p.columns) ? p.columns : [],
    rows: Array.isArray(p.rows) ? p.rows : [],
    total: typeof p.total === "number" ? p.total : undefined,
  }
  return { rows, currency: p.currency ?? currency }
}

const toSnapshotPayload = (
  resolved: ResolvedWidget,
  currency: string | undefined
): WidgetPayload => {
  const p = resolved.payload as Partial<SnapshotPayload> & { currency?: string }
  return {
    snapshot: {
      rows: Array.isArray(p.rows) ? p.rows : [],
      columns: Array.isArray(p.columns) ? p.columns : [],
      cells: Array.isArray(p.cells) ? p.cells : [],
      ...(p.filters_ignored?.length ? { filters_ignored: p.filters_ignored } : {}),
    },
    currency: p.currency ?? currency,
  }
}

export interface AdaptedWidget {
  widget: WidgetInstance
  payload: WidgetPayload
  valueKey: string
  metric?: string
}

export const adaptResolved = (
  resolved: ResolvedWidget,
  specIndex: SpecIndex,
  currency?: string
): AdaptedWidget => {
  const metric = metricOf(resolved)
  const spec = specFor(resolved, specIndex)
  const capabilities = resolved.capabilities as WidgetInstance["capabilities"]

  let payload: WidgetPayload
  if ((resolved.payload as { no_access?: boolean })?.no_access) payload = { no_access: true }
  else if (resolved.type === "kpi") payload = toKpiPayload(resolved, spec, currency)
  else if (resolved.type === "chart") payload = toChartPayload(resolved)
  else if (resolved.type === "feed") payload = toFeedPayload(resolved)
  else if (resolved.type === "alert") payload = toAlertPayload(resolved)
  else if (resolved.type === "table" || resolved.type === "list")
    payload = toRowsPayload(resolved, currency)
  else if (resolved.type === "snapshot") payload = toSnapshotPayload(resolved, currency)
  else payload = {}

  const widget: WidgetInstance = {
    id: resolved.id,
    type: resolved.type as WidgetInstance["type"],
    dataSource: spec?.data_source,
    title: spec?.title ?? fallbackTitle(resolved),
    config: resolved.config as WidgetConfig,
    capabilities,
    drill_to: resolved.drill_to || spec?.drill_to || undefined,
  }
  return { widget, payload, valueKey: resolvedValueKey(resolved), metric }
}

export const currencyFromWidgets = (widgets: ResolvedWidget[]): string | undefined => {
  for (const w of widgets) {
    const c = (w.payload as { currency?: string })?.currency
    if (typeof c === "string" && c) return c
  }
  return undefined
}

export type WidgetCacheEntry = AdaptedWidget

export const buildResolvedCache = (
  widgets: ResolvedWidget[],
  specIndex: SpecIndex,
  currency?: string
): Record<string, WidgetCacheEntry> => {
  const cache: Record<string, WidgetCacheEntry> = {}
  for (const resolved of widgets) {
    const adapted = adaptResolved(resolved, specIndex, currency)
    cache[adapted.valueKey] = adapted
  }
  return cache
}

// v3 tile-id (a KPI metric) -> v4 widget instance, resolved through the catalog.
export const liftTileToInstance = (
  tileId: string,
  specIndex: SpecIndex
): WidgetInstanceV4 | null => {
  const spec = specIndex.byMetric.get(tileId)
  if (!spec || spec.type !== "kpi") return null
  const slug = slugFor(spec.data_source, spec.metric, spec.type)
  return {
    id: slug,
    slug,
    type: spec.type,
    dataSource: spec.data_source,
    config: { metric: tileId },
  }
}

export const specToInstance = (spec: CatalogWidgetSpec): WidgetInstanceV4 => {
  const slug = slugFor(spec.data_source, spec.metric, spec.type)
  return {
    id: slug,
    slug,
    type: spec.type,
    dataSource: spec.data_source,
    config: spec.metric ? { metric: spec.metric } : {},
  }
}

// KPI specs eligible for the "+ Add" library (fixed chart/feed/stub excluded).
export const catalogKpiSpecs = (catalog: WidgetCatalog | null): CatalogWidgetSpec[] =>
  (catalog?.widgets ?? []).filter((s) => s.type === "kpi")

export const isKpiSlug = (slug: string): boolean => slug.endsWith(".kpi")

export const typeFromSlug = (slug: string): string => slug.split(".").pop() ?? ""

export const isSnapshotSlug = (slug: string): boolean => typeFromSlug(slug) === "snapshot"

export const catalogSnapshotSpecs = (catalog: WidgetCatalog | null): CatalogWidgetSpec[] =>
  (catalog?.widgets ?? []).filter((s) => s.type === "snapshot")

// Charts a user may place on the board. Named one by one: Cash Flow and
// Project Status are charts too, but they live in fixed slots and must not
// also be offered as board cards.
const BOARD_CHART_SLUGS: ReadonlySet<string> = new Set([
  "leads.lead_engagement.chart",
  "leads.marketing_source_performance.chart"
])

// A board chart too wide for the 68% column: it takes a full-width row of its own.
const WIDE_BOARD_SLUGS: ReadonlySet<string> = new Set([
  "leads.marketing_source_performance.chart"
])

export const isWideBoardSlug = (slug: string): boolean => WIDE_BOARD_SLUGS.has(slug)

/** Board charts in the saved order, as rows: a wide chart alone on its row,
 *  and the charts between wide ones sharing the 68% column. */
export const boardChartRows = (order: string[]): { wide: boolean; slugs: string[] }[] => {
  const rows: { wide: boolean; slugs: string[] }[] = []
  for (const slug of order) {
    const wide = isWideBoardSlug(slug)
    const last = rows[rows.length - 1]
    if (!wide && last && !last.wide) last.slugs.push(slug)
    else rows.push({ wide, slugs: [slug] })
  }
  return rows
}

export const isBoardSlug = (slug: string): boolean => {
  const type = typeFromSlug(slug)
  return type === "table" || type === "list" || BOARD_CHART_SLUGS.has(slug)
}

export const catalogBoardSpecs = (catalog: WidgetCatalog | null): CatalogWidgetSpec[] =>
  (catalog?.widgets ?? []).filter(
    (s) =>
      s.type === "table" ||
      s.type === "list" ||
      BOARD_CHART_SLUGS.has(slugFor(s.data_source, s.metric, s.type))
  )

// The Next-Best-Actions feed is rendered in a fixed slot, not the add library.
const FIXED_FEED_SOURCE = "feed"

export const sourceFromSlug = (slug: string): string => slug.split(".")[0] ?? ""

export const isFeedAlertSlug = (slug: string): boolean => {
  const type = typeFromSlug(slug)
  if (type !== "feed" && type !== "alert") return false
  return sourceFromSlug(slug) !== FIXED_FEED_SOURCE
}

export const catalogFeedAlertSpecs = (
  catalog: WidgetCatalog | null
): CatalogWidgetSpec[] =>
  (catalog?.widgets ?? []).filter(
    (s) =>
      (s.type === "feed" || s.type === "alert") && s.data_source !== FIXED_FEED_SOURCE
  )

export const metricFromSlug = (slug: string): string => slug.split(".")[1] ?? ""

export const resolvedSlug = (
  resolved: ResolvedWidget,
  specIndex: SpecIndex
): string | null => {
  const metric = metricOf(resolved)
  if (!metric) return null
  const spec = specIndex.byMetric.get(metric)
  return slugFor(spec?.data_source ?? "", metric, resolved.type)
}

export const responseToInstances = (
  widgets: ResolvedWidget[],
  specIndex: SpecIndex
): WidgetInstanceV4[] => {
  const out: WidgetInstanceV4[] = []
  for (const resolved of widgets) {
    const metric = metricOf(resolved)
    if (!metric) continue
    const spec = specIndex.byMetric.get(metric)
    if (!spec) continue
    const slug = slugFor(spec.data_source, metric, resolved.type)
    out.push({
      id: resolved.id ?? slug,
      slug,
      type: resolved.type,
      dataSource: spec.data_source,
      config: resolved.config,
    })
  }
  return out
}
